import { config } from './config';

let scriptLoadPromise;

/**
 * Loads Google reCAPTCHA v3 when REACT_APP_RECAPTCHA_SITE_KEY is set.
 * @returns {Promise<void>}
 */
export function ensureRecaptchaLoaded() {
  const siteKey = config.RECAPTCHA_SITE_KEY;
  if (!siteKey) {
    return Promise.resolve();
  }
  if (typeof document === 'undefined') {
    return Promise.resolve();
  }
  if (typeof window !== 'undefined' && window.grecaptcha?.execute) {
    return Promise.resolve();
  }
  if (scriptLoadPromise) {
    return scriptLoadPromise;
  }
  scriptLoadPromise = new Promise((resolve, reject) => {
    const existing = document.querySelector(
      'script[src^="https://www.google.com/recaptcha/api.js"]'
    );
    if (existing) {
      existing.addEventListener('load', () => resolve());
      existing.addEventListener('error', reject);
      return;
    }
    const s = document.createElement('script');
    s.src = `https://www.google.com/recaptcha/api.js?render=${encodeURIComponent(siteKey)}`;
    s.async = true;
    s.defer = true;
    s.onload = () => resolve();
    s.onerror = () => reject(new Error('Failed to load reCAPTCHA'));
    document.head.appendChild(s);
  });
  return scriptLoadPromise;
}

/**
 * Returns a v3 token for the given action, or empty string if reCAPTCHA is not configured.
 * @param {string} action
 * @returns {Promise<string>}
 */
export async function getRecaptchaToken(action) {
  const siteKey = config.RECAPTCHA_SITE_KEY;
  if (!siteKey || typeof window === 'undefined') {
    return '';
  }
  try {
    await ensureRecaptchaLoaded();
  } catch (e) {
    console.error('reCAPTCHA failed to load', e);
    return '';
  }
  if (!window.grecaptcha?.execute) {
    return '';
  }
  try {
    return await window.grecaptcha.execute(siteKey, { action });
  } catch (e) {
    console.error('reCAPTCHA execute failed', e);
    return '';
  }
}
